import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../lib/auth/AuthContext';

interface RequireAuthProps {
  children: React.ReactNode;
  /** Bypass the onboarding gate. Used by /onboard itself + /confirm. */
  allowWithoutAccount?: boolean;
}

/**
 * Route guard.
 *
 * 1. Unauthenticated → /login (with `from` state for the bounce-back).
 * 2. Authenticated but no account yet → /onboard.
 * 3. Authenticated + onboarded → render the protected page.
 */
export const RequireAuth: React.FC<RequireAuthProps> = ({ children, allowWithoutAccount }) => {
  const { isAuthenticated, me, meLoading } = useAuth();
  const location = useLocation();

  if (!isAuthenticated) {
    return <Navigate to="/login" replace state={{ from: location }} />;
  }

  // Wait for /auth/me to resolve before deciding whether to bounce to
  // onboarding — otherwise we'd flash protected pages and immediately
  // redirect when the call comes back.
  if (!allowWithoutAccount) {
    if (meLoading) {
      return <div className="text-sm text-slate-500 p-6">Loading workspace…</div>;
    }
    if (me && me.accounts.length === 0) {
      return <Navigate to="/onboard" replace />;
    }
    // Redirect owners with an incomplete onboarding wizard back to /onboard
    if (me && me.accounts.length > 0) {
      const currentAccount = me.accounts[0];
      if (
        currentAccount.role === 'owner' &&
        currentAccount.onboarding &&
        currentAccount.onboarding.step !== 'completed'
      ) {
        return <Navigate to="/onboard" replace />;
      }
    }
  }

  return <>{children}</>;
};
